/*  TWO COLUMNS WITH VIDEO (local file or external provider)
    Inspired by: https://tailwindui.com/components/marketing/sections/content-sections#component-39249d619dcd4a64f3089bf550369572
*/

import React, { useEffect, useRef, useState } from 'react'
import SbEditable from 'storyblok-react'
import RichText from 'src/utils/RichText'
import Label from 'src/components/Label'
import Heading from 'src/components/Heading'
import { PlayIcon, VideoCameraIcon } from '@heroicons/react/solid'
import { Link } from 'gatsby'
import resolveLink from 'src/utils/resolveLink'
import FluidImage from 'src/utils/FluidImage'

/**
 * Expected blok fields (suggested):
 * - label (text)
 * - title (text)
 * - title_h_tag (option: h1..h6)
 * - content (richtext)
 * - flip (boolean) – swaps columns
 * - caption_location (text)
 * - caption_target (link object) – optional; when present caption is a link
 * - video_url (text) – external (YouTube/Vimeo/etc)
 * - video_file (asset) – uploaded mp4/webm/etc
 * - video (asset) – legacy fallback (either uploaded file or external URL)
 * - poster (asset) – poster image for <video>
 * - thumbnail (asset) – optional click-to-play overlay image
 * - autoplay, controls, loop, muted, playsinline (booleans)
 */

const buildExternalEmbed = (url = '') => {
  if (!url) return null

  try {
    const u = new URL(url)

    // YouTube (watch or short forms)
    if (/(youtube\.com|youtu\.be)/i.test(u.hostname)) {
      // Normalize to embed
      let id = ''
      if (u.hostname.includes('youtu.be')) {
        id = u.pathname.replace('/', '')
      } else if (u.searchParams.get('v')) {
        id = u.searchParams.get('v')
      } else if (u.pathname.startsWith('/shorts/')) {
        id = u.pathname.split('/')[2]
      }
      if (!id) return null
      const params = new URLSearchParams({
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
      })
      return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`
    }

    // Vimeo
    if (/vimeo\.com/i.test(u.hostname)) {
      const match = u.pathname.match(/\/(\d+)/)
      if (!match) return null
      const id = match[1]
      return `https://player.vimeo.com/video/${id}`
    }

    // Fallback – try to use the URL directly in an iframe
    return url
  } catch (e) {
    return null
  }
}

const ResponsiveEmbed = ({ src, title = 'Embedded video', autoPlay = false }) => {
  // Uses a 16:9 wrapper. Requires @tailwindcss/aspect-ratio if available.
  // If not available, the inline padding-bottom hack keeps the ratio.
  let resolvedSrc = src

  if (src && autoPlay) {
    try {
      const embedUrl = new URL(src)
      embedUrl.searchParams.set('autoplay', '1')
      resolvedSrc = embedUrl.toString()
    } catch (error) {
      const joiner = src.includes('?') ? '&' : '?'
      resolvedSrc = `${src}${joiner}autoplay=1`
    }
  }

  return (
    <div className="relative w-full">
      <div className="aspect-w-16 aspect-h-9">
        <iframe
          className="w-full h-full rounded-lg shadow"
          src={resolvedSrc}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  )
}

const LocalVideo = ({
  src,
  poster,
  autoplay = false,
  controls = true,
  loop = false,
  muted = false,
  playsInline = true,
  videoRef,
}) => (
  <video
    ref={videoRef}
    className="w-full h-auto rounded-lg shadow"
    src={src}
    poster={poster}
    autoPlay={autoplay}
    controls={controls}
    loop={loop}
    muted={muted}
    playsInline={playsInline}
  />
)

const TwoColumnsWithVideo = ({ blok }) => {
  const caption = blok.caption_location
    ? blok.caption_target
      ? (
          <Link
            to={resolveLink(blok.caption_target.cached_url)}
            className="font-semibold text-pink hover:text-darkblue duration-300"
          >
            {blok.caption_location}
          </Link>
        )
      : (
          <span className="font-semibold text-pink">{blok.caption_location}</span>
        )
    : null

  const poster = blok.poster?.filename
  const videoAsset = blok.video_file || blok.video
  const uploadedSrc =
    videoAsset?.filename && videoAsset?.is_external_url !== true ? videoAsset.filename : null
  const externalCandidate =
    blok.video_url || (videoAsset?.is_external_url ? videoAsset.filename : null)
  const externalSrc = buildExternalEmbed(externalCandidate)

  const showExternal = !!externalSrc
  const showLocal = !showExternal && !!uploadedSrc
  const hasThumbnail = !!blok.thumbnail?.filename
  const canPlayVideo = showLocal || showExternal
  const [showVideo, setShowVideo] = useState(!hasThumbnail || !canPlayVideo)
  const [shouldAutoPlayEmbed, setShouldAutoPlayEmbed] = useState(false)
  const localVideoRef = useRef(null)

  useEffect(() => {
    setShowVideo(!hasThumbnail || !canPlayVideo)
    setShouldAutoPlayEmbed(false)
  }, [hasThumbnail, canPlayVideo])

  const showThumbnail = hasThumbnail && !showVideo && canPlayVideo
  const handleShowVideo = () => {
    setShowVideo(true)

    if (showLocal && localVideoRef.current?.play) {
      const maybePromise = localVideoRef.current.play()
      if (maybePromise?.catch) {
        maybePromise.catch(() => {})
      }
    }

    if (showExternal) {
      setShouldAutoPlayEmbed(true)
    }
  }

  return (
    <SbEditable content={blok}>
      <div className="bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-base max-w-prose lg:grid lg:grid-cols-2 lg:gap-8 lg:max-w-none">
            <div>
              <Label className="mb-2 text-pink">{blok.label}</Label>
              <Heading
                size="h1"
                as={blok.title_h_tag ? blok.title_h_tag : 'h2'}
                className="text-darkblue"
              >
                {blok.title}
              </Heading>
            </div>
          </div>

          <div className="mt-6 lg:grid lg:grid-cols-2 lg:gap-16">
            <div className={`relative ${blok.flip ? '' : 'lg:row-start-1 lg:col-start-2'}`}>
              <div className="relative text-base">
                <figure>
                  <div className="relative">
                    {showLocal && (
                      <LocalVideo
                        src={uploadedSrc}
                        poster={poster}
                        autoplay={!!blok.autoplay}
                        controls={blok.controls !== false}
                        loop={!!blok.loop}
                        muted={!!blok.muted}
                        playsInline={blok.playsinline !== false}
                        videoRef={localVideoRef}
                      />
                    )}

                    {!showLocal && showExternal && (
                      <ResponsiveEmbed
                        src={externalSrc}
                        title={blok.title || 'Video'}
                        autoPlay={shouldAutoPlayEmbed}
                      />
                    )}

                    {!showLocal && !showExternal && (
                      <div className="w-full h-48 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                        <span>No video source provided</span>
                      </div>
                    )}

                    {showThumbnail && (
                      <button
                        type="button"
                        onClick={handleShowVideo}
                        className="absolute inset-0 z-10 block w-full h-full rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink overflow-hidden"
                        aria-label="Play video"
                      >
                        <FluidImage
                          className="w-full h-full rounded-lg object-cover"
                          image={blok.thumbnail}
                        />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full text-pink">
                            <PlayIcon className="w-16 h-16 sm:w-20 sm:h-20 text-pink" aria-hidden="true" />
                          </span>
                        </span>
                      </button>
                    )}
                  </div>

                  {caption && (
                    <figcaption className="mt-3 flex text-sm text-gray font-body items-center">
                      <VideoCameraIcon className="flex-none w-5 h-5 text-pink" aria-hidden="true" />
                      <span className="ml-2 leading-normal">Videography at {caption}</span>
                    </figcaption>
                  )}
                </figure>
              </div>
            </div>

            <div className="mt-8 lg:mt-0">
              <div className="text-base max-w-prose mx-auto lg:max-w-none" />
              <div className="mt-5 prose text-gray leading-6 font-body lg:row-start-1 lg:col-start-1">
                <RichText>{blok.content}</RichText>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SbEditable>
  )
}

export default TwoColumnsWithVideo
